import React, { useMemo, useState } from 'react';
import { HeroSection } from '../components/HeroSection';
import { Search, ChevronDown, ChevronUp, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
interface FAQItem {
  question: string;
  answer: string;
  category: 'courses' | 'enrollment' | 'accessibility' | 'payment' | 'content' | 'general';
}
export function FAQ() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(null);
  const faqs: FAQItem[] = [{
    question: 'What is ReGrowth?',
    answer: 'ReGrowth is a fat liberation education and publishing platform. We offer courses, workshops, and a digital publication centered on fat justice, anti-oppression politics, and collective liberation. Our work is grounded in intersectional feminism, disability justice, and anti-racism.',
    category: 'general'
  }, {
    question: 'Who are your courses for?',
    answer: 'Our courses are designed for anyone committed to fat liberation and anti-oppression work. This includes fat people seeking community and education, thin people wanting to be better accomplices, educators, healthcare providers, and organizers. We especially encourage participation from fat Black, Indigenous, disabled, trans, and working-class people.',
    category: 'courses'
  }, {
    question: 'Do I need to be fat to take your courses?',
    answer: 'No. Thin people are welcome in our courses as learners and accomplices. However, we center fat voices and experiences, and we expect thin participants to listen more than they speak, especially in discussions about lived experience. Some courses are specifically for fat people only—this will be clearly marked in the course description.',
    category: 'courses'
  }, {
    question: 'How do I enroll in a course?',
    answer: 'Browse our course catalog, select the course you want, and click "Enroll Now." You\'ll create an account (if you don\'t have one), choose your payment option (including sliding scale), and receive immediate access to self-paced courses or a calendar invite for cohort-based courses. We send a confirmation email with all the details.',
    category: 'enrollment'
  }, {
    question: 'What is sliding scale pricing?',
    answer: 'Sliding scale means you choose what you can afford to pay within a given range. Those who can pay more subsidize those who can pay less. We trust you to assess your own financial situation honestly. If even the lowest tier is out of reach, apply for a full scholarship—we never turn anyone away for lack of funds.',
    category: 'payment'
  }, {
    question: 'How do I apply for a scholarship?',
    answer: 'Email us at scholarships@regrowth.com with the course name and a brief note about your financial situation. No lengthy applications or proof required—we trust you. We typically respond within 2 business days with a scholarship code. Scholarships cover 100% of course fees.',
    category: 'payment'
  }, {
    question: "What's the difference between cohort-based and self-paced courses?",
    answer: 'Cohort-based courses have set start/end dates, live sessions, and a small group of peers learning together. Self-paced courses are available anytime, have no live sessions, and you work through materials on your own schedule. Both include discussion forums and facilitator support.',
    category: 'courses'
  }, {
    question: 'Are your courses accessible?',
    answer: 'Yes. All courses include captions, transcripts, and alternative formats for materials. Live sessions are recorded and available within 24 hours. We provide accommodations upon request (ASL interpretation, extended time, etc.). If you need specific accommodations, email access@regrowth.com at least 2 weeks before the course starts.',
    category: 'accessibility'
  }, {
    question: 'Can I get a refund?',
    answer: "For cohort-based courses: Full refund if you withdraw before the first session. 50% refund if you withdraw within the first week. No refunds after week one. For self-paced courses: Full refund within 7 days of purchase if you haven't completed more than 25% of the content. Email refunds@regrowth.com.",
    category: 'payment'
  }, {
    question: 'Do you offer certificates of completion?',
    answer: "Yes. All courses include a certificate of completion when you finish. Certificates include the course name, completion date, and total hours. They're downloadable as PDFs. Note: Our certificates are for personal/professional development, not academic credit.",
    category: 'courses'
  }, {
    question: 'How can I submit writing to The Publication?',
    answer: 'Visit our Contribute page for full submission guidelines. We accept essays, reporting, poetry, visual work, and hybrid forms. Send submissions to submissions@regrowth.com with "SUBMISSION: [Type]" in the subject line. We pay all contributors and respond within 4-8 weeks.',
    category: 'content'
  }, {
    question: 'Do you pay contributors?',
    answer: 'Yes! We pay $0.25/word for essays and reporting (minimum $375), $100 per poem, and $200-500 for visual work. Payment is issued within 30 days of publication. We believe in compensating creative and intellectual labor.',
    category: 'content'
  }, {
    question: 'Can I use ReGrowth content in my classroom or workshop?',
    answer: 'Yes, with attribution. Our blog posts and free resources can be shared for educational purposes with proper citation. Paid course materials are for personal use only and cannot be redistributed. If you want to use our materials in a paid workshop or training, email licensing@regrowth.com.',
    category: 'content'
  }, {
    question: 'Are your courses live or recorded?',
    answer: 'It depends on the course type. Cohort-based courses have live sessions (recorded for later viewing). Self-paced courses are entirely pre-recorded. Workshop-style events are typically live with recordings available to registrants for 30 days.',
    category: 'courses'
  }, {
    question: 'What if I miss a live session?',
    answer: 'No problem! All live sessions are recorded and available within 24 hours. Recordings include captions and transcripts. You can watch at your own pace and still participate in async discussions. We understand that life happens.',
    category: 'courses'
  }, {
    question: 'Do you offer group discounts?',
    answer: 'Yes. For organizations enrolling 5+ people in the same course, email groups@regrowth.com for pricing. We offer discounts for nonprofits, grassroots organizations, and groups led by/for marginalized communities.',
    category: 'payment'
  }, {
    question: 'Can I gift a course to someone?',
    answer: 'Absolutely! During checkout, select "This is a gift" and enter the recipient\'s email. They\'ll receive a gift notification with enrollment instructions. You can also purchase a gift certificate for any amount at regrowth.com/gift.',
    category: 'enrollment'
  }, {
    question: 'What are your community guidelines?',
    answer: 'We expect all participants to engage respectfully, center marginalized voices, practice accountability, and respect boundaries. We have zero tolerance for fatphobia, racism, ableism, transphobia, or harassment. Full guidelines are sent with course enrollment and available at regrowth.com/policies/community.',
    category: 'general'
  }, {
    question: 'What happens if someone violates community guidelines?',
    answer: 'Violations are addressed case-by-case. Minor issues result in a warning and conversation. Serious or repeated violations result in removal from the course without refund. We prioritize the safety of marginalized participants.',
    category: 'general'
  }, {
    question: 'Can I access course materials after the course ends?',
    answer: "Yes! For cohort-based courses, you have lifetime access to recordings, materials, and resources. For self-paced courses, you have lifetime access to all content. We may update materials over time, and you'll get those updates automatically.",
    category: 'courses'
  }, {
    question: 'Do you offer private training for organizations?',
    answer: 'Yes. We provide custom workshops and training for organizations, schools, healthcare systems, and businesses. Topics include fat liberation 101, inclusive healthcare, accessible design, and more. Email training@regrowth.com for pricing and availability.',
    category: 'general'
  }, {
    question: 'How do I cancel my newsletter subscription?',
    answer: 'Click "unsubscribe" at the bottom of any newsletter email. You\'ll be removed immediately. You can also manage your email preferences in your account settings.',
    category: 'general'
  }];
  const categories = [{
    id: 'all',
    label: 'All Questions',
    color: 'bg-brand-navy'
  }, {
    id: 'courses',
    label: 'Courses',
    color: 'bg-brand-cyan'
  }, {
    id: 'enrollment',
    label: 'Enrollment',
    color: 'bg-brand-magenta'
  }, {
    id: 'payment',
    label: 'Payment & Pricing',
    color: 'bg-brand-lime'
  }, {
    id: 'accessibility',
    label: 'Accessibility',
    color: 'bg-brand-orange'
  }, {
    id: 'content',
    label: 'Content & Publishing',
    color: 'bg-brand-cyan'
  }, {
    id: 'general',
    label: 'General',
    color: 'bg-brand-navy'
  }];
  const filteredFAQs = useMemo(() => {
    let filtered = faqs;
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(faq => faq.category === selectedCategory);
    }
    if (searchQuery) {
      filtered = filtered.filter(faq => faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    return filtered;
  }, [searchQuery, selectedCategory]);
  const toggleQuestion = (index: number) => {
    setExpandedQuestion(expandedQuestion === index ? null : index);
  };
  return <div className="min-h-screen bg-brand-cream" data-id="element-1905">
      <HeroSection title="Frequently Asked Questions" subtitle="Quick answers to common questions about courses, enrollment, and our work." tag="Help Center" bgColor="navy" data-id="element-1906" />

      {/* Search & Filter */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto -mt-8 bg-white relative z-10 shadow-lg border-2 border-brand-black" data-id="element-1907">
        <div className="space-y-6" data-id="element-1908">
          {/* Search */}
          <div className="relative" data-id="element-1909">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-1910" />
            <input type="text" placeholder="Search questions..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 focus:border-brand-navy focus:outline-none text-lg" data-id="element-1911" />
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2" data-id="element-1912">
            {categories.map(category => <button key={category.id} onClick={() => setSelectedCategory(category.id)} className={`px-4 py-2 text-xs font-bold uppercase tracking-wide transition-all border-2 ${selectedCategory === category.id ? `${category.color} text-white border-transparent` : 'bg-white text-gray-600 border-gray-200 hover:border-brand-navy'}`} data-id="element-1913">
                {category.label}
              </button>)}
          </div>

          <div className="text-sm text-gray-500" data-id="element-1914">
            {filteredFAQs.length}{' '}
            {filteredFAQs.length === 1 ? 'question' : 'questions'}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1915">
        <div className="space-y-4" data-id="element-1916">
          {filteredFAQs.map((faq, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.05
        }} className="border-2 border-brand-black bg-white" data-id="element-1917">
              <button onClick={() => toggleQuestion(index)} className="w-full p-6 flex items-start justify-between text-left hover:bg-gray-50 transition-colors" data-id="element-1918">
                <h3 className="text-lg font-bold text-brand-black pr-8 flex-1" data-id="element-1919">
                  {faq.question}
                </h3>
                <div className="flex-shrink-0" data-id="element-1920">
                  {expandedQuestion === index ? <ChevronUp className="h-6 w-6 text-brand-navy" data-id="element-1921" /> : <ChevronDown className="h-6 w-6 text-gray-400" data-id="element-1922" />}
                </div>
              </button>

              <AnimatePresence data-id="element-1923">
                {expandedQuestion === index && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-1924">
                    <div className="px-6 pb-6 pt-2 border-t-2 border-gray-100" data-id="element-1925">
                      <p className="text-gray-700 leading-relaxed" data-id="element-1926">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>

        {filteredFAQs.length === 0 && <div className="text-center py-16" data-id="element-1927">
            <p className="text-xl text-gray-500 mb-4" data-id="element-1928">
              No questions found matching "{searchQuery}"
            </p>
            <button onClick={() => {
          setSearchQuery('');
          setSelectedCategory('all');
        }} className="text-brand-navy font-bold hover:underline" data-id="element-1929">
              Clear filters
            </button>
          </div>}
      </section>

      {/* Still Have Questions */}
      <section className="py-20 bg-brand-navy text-white" data-id="element-1930">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1931">
          <MessageCircle className="h-12 w-12 mx-auto mb-6 text-brand-cyan" data-id="element-1932" />
          <h2 className="text-3xl font-black uppercase mb-6" data-id="element-1933">
            Still Have Questions?
          </h2>
          <p className="text-xl mb-8 text-gray-200" data-id="element-1934">
            Can't find what you're looking for? We're here to help.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1935">
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-brand-cyan text-brand-navy font-bold uppercase tracking-wide hover:bg-brand-cyan/90 transition-colors" data-id="element-1936">
              Contact Us
            </Link>
            <a href="mailto:hello@regrowth.com" className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-bold uppercase tracking-wide hover:bg-white hover:text-brand-navy transition-colors" data-id="element-1937">
              Email Us
            </a>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-white" data-id="element-1938">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1939">
          <h2 className="text-2xl font-black uppercase mb-8 text-brand-black text-center" data-id="element-1940">
            Helpful Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1941">
            <Link to="/clusters/courses" className="group border-2 border-brand-black p-6 hover:shadow-lg transition-shadow" data-id="element-1942">
              <h3 className="font-bold text-lg mb-2 text-brand-black group-hover:text-brand-cyan transition-colors" data-id="element-1943">
                Browse Courses
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1944">
                Explore our full course catalog and find the right learning path
                for you.
              </p>
            </Link>

            <Link to="/policies" className="group border-2 border-brand-black p-6 hover:shadow-lg transition-shadow" data-id="element-1945">
              <h3 className="font-bold text-lg mb-2 text-brand-black group-hover:text-brand-cyan transition-colors" data-id="element-1946">
                Policies
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1947">
                Read our accessibility, privacy, and community guidelines.
              </p>
            </Link>

            <Link to="/clusters/contribute" className="group border-2 border-brand-black p-6 hover:shadow-lg transition-shadow" data-id="element-1948">
              <h3 className="font-bold text-lg mb-2 text-brand-black group-hover:text-brand-cyan transition-colors" data-id="element-1949">
                Submit Work
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1950">
                Learn how to contribute writing, art, or other work to The
                Publication.
              </p>
            </Link>
          </div>
        </div>
      </section>
    </div>;
}